import { ServicePageReviews_productizedService$key } from '#app/__generated__/relay/ServicePageReviews_productizedService.graphql.js';
import { Text } from '#app/components/Primitives/Text.js';
import { UserReviews } from '#app/components/UserReview/UserReviews.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import {
  RecommendationStarFullIcon,
  RecommendationStarHalfIcon,
  StarIcon,
} from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { css, styled } from 'styled-components';

const Container = styled.div`
  width: 100%;
`;

const ReviewsHeader = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const ReviewsHeaderTitle = styled(Text)``;

const StandaloneRatingContainer = styled.div`
  ${({ theme }) => css`
    border-radius: 5px;
    border: 1px solid ${theme.colorsV2.gray30};
    padding: 6px 8px;
    display: flex;
    gap: 6px;
    align-items: center;
    width: fit-content;
  `}
`;

const StandaloneStarContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const StarContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  margin-left: 12px;
  margin-right: 6px;
`;

const Star = styled(RecommendationStarFullIcon)`
  ${({ theme }) => css`
    path {
      fill: ${theme.colorsV2.gray80};
    }
  `}
`;

const HalfStar = styled(RecommendationStarHalfIcon)`
  ${({ theme }) => css`
    path {
      fill: ${theme.colorsV2.gray80};
    }
  `}
`;

const ReviewsList = styled.div<{ $noBorder: boolean }>`
  ${({ $noBorder, theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin-top: 40px;
    ${$noBorder
      ? ''
      : css`
          border-bottom: 1px solid ${theme.colorsV2.gray30};
        `}
  `}
`;

type Props = {
  readonly hideHeader?: boolean;
  readonly productizedServiceRef: ServicePageReviews_productizedService$key;
};

export const ServicePageReviews = ({
  hideHeader = false,
  productizedServiceRef,
}: Props) => {
  const service = useFragment<ServicePageReviews_productizedService$key>(
    graphql`
      fragment ServicePageReviews_productizedService on ProductizedService {
        ...ServicePageCheckoutHeader_productizedService
        ...ServiceCheckout_productizedService
        userProfile {
          ...UserReviews_userProfile
          reviews: userReviews(
            filter: { isPositiveReview: true, onlyVisibleToRecipient: false }
          ) {
            edges {
              node {
                commentOnlyVisibleToRecipient
                onlyVisibleToRecipient
                isPositiveReview
                rating
              }
            }
          }
        }
      }
    `,
    productizedServiceRef,
  );

  const isMobile = useIsMobile();

  const reviewEdgesThatCanBeUsed = service.userProfile.reviews.edges.filter(
    ({
      node: {
        commentOnlyVisibleToRecipient,
        isPositiveReview,
        onlyVisibleToRecipient,
      },
    }) =>
      isPositiveReview &&
      !onlyVisibleToRecipient &&
      !commentOnlyVisibleToRecipient,
  );

  const averageRating =
    reviewEdgesThatCanBeUsed.reduce(
      (total, { node: { rating } }) => total + rating,
      0,
    ) / reviewEdgesThatCanBeUsed.length;

  const fullStarCount = Math.floor(averageRating);
  const hasHalfStar = averageRating % 1 > 0;
  const emptyStarCount = 5 - fullStarCount - (hasHalfStar ? 1 : 0);

  const stars = Array.from({ length: fullStarCount }, (_, index) => (
    <Star
      key={index}
      size={12}
    />
  ));

  const emptyStars = Array.from({ length: emptyStarCount }, (_, index) => (
    <StarIcon
      key={index}
      size={13}
      style={{ opacity: 0.5 }}
    />
  ));

  if (reviewEdgesThatCanBeUsed.length === 0) {
    return null;
  }

  return (
    <Container id="user-recommendations">
      {hideHeader ? (
        <StandaloneRatingContainer>
          <StandaloneStarContainer>
            {stars}
            {hasHalfStar ? <HalfStar size={12} /> : null}
            {emptyStars}
          </StandaloneStarContainer>

          <Text textStyle="bodySmall">({averageRating.toFixed(1)})</Text>
        </StandaloneRatingContainer>
      ) : (
        <ReviewsHeader>
          <ReviewsHeaderTitle
            textStyle={isMobile ? 'overline' : 'sixthLevelHeadline'}
          >
            Recommendations
          </ReviewsHeaderTitle>
          <StarContainer>
            {stars}
            {hasHalfStar ? <HalfStar size={12} /> : null}
            {emptyStars}
          </StarContainer>
          <Text textStyle="bodySmall">({averageRating.toFixed(1)})</Text>
        </ReviewsHeader>
      )}

      <ReviewsList $noBorder>
        <UserReviews profileKey={service.userProfile} />
      </ReviewsList>
    </Container>
  );
};
